import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react'
import type {
  LumpsumPlanRecommendationHighExposure,
  LumpsumPlanRecommendationOverDiversification,
  LumpsumPlanRecommendationOverExposure,
  LumpsumPlanRecommendationUnderPerformance
} from '../../api/recommendation.types'
import {
  BetterReturnsPointer,
  ExcellentRankPointer,
  IncreaseExposurePointer,
  MissedGainsPointer,
  ReduceExposurePointer,
  ReduceOverDiversificationPointer,
  ReduceOverExposurePointer,
  type ReasonPointerData
} from './ReasonPointerUi'

export type { ReasonPointerData }

export enum ReasonsAccordionPlanType {
  REBALANCE = 'REBALANCE',
  REALLOCATE = 'REALLOCATE'
}

export enum ReasonsToRebalanceState {
  NONE = 'NONE',
  /** Figma: over exposed in a fund — backend: highExposureCase */
  HIGH_EXPOSURE = 'HIGH_EXPOSURE',
  /** Figma: high exposure in a category — backend: overExposureCase */
  OVER_EXPOSURE = 'OVER_EXPOSURE',
  OVER_DIVERSIFICATION = 'OVER_DIVERSIFICATION',
  HIGH_EXPOSURE_OVER_EXPOSURE = 'HIGH_EXPOSURE_OVER_EXPOSURE',
  HIGH_EXPOSURE_OVER_DIVERSIFICATION = 'HIGH_EXPOSURE_OVER_DIVERSIFICATION',
  OVER_EXPOSURE_OVER_DIVERSIFICATION = 'OVER_EXPOSURE_OVER_DIVERSIFICATION',
  HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION = 'HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION',
  UNDERPERFORMANCE_MG = 'UNDERPERFORMANCE_MG',
  UNDERPERFORMANCE_NO_MG = 'UNDERPERFORMANCE_NO_MG',
  HIGH_EXPOSURE_MG = 'HIGH_EXPOSURE_MG',
  HIGH_EXPOSURE_NO_MG = 'HIGH_EXPOSURE_NO_MG',
  OVER_EXPOSURE_MG = 'OVER_EXPOSURE_MG',
  OVER_EXPOSURE_NO_MG = 'OVER_EXPOSURE_NO_MG',
  OVER_DIVERSIFICATION_MG = 'OVER_DIVERSIFICATION_MG',
  OVER_DIVERSIFICATION_NO_MG = 'OVER_DIVERSIFICATION_NO_MG',
  HIGH_EXPOSURE_OVER_EXPOSURE_MG = 'HIGH_EXPOSURE_OVER_EXPOSURE_MG',
  HIGH_EXPOSURE_OVER_EXPOSURE_NO_MG = 'HIGH_EXPOSURE_OVER_EXPOSURE_NO_MG',
  HIGH_EXPOSURE_OVER_DIVERSIFICATION_MG = 'HIGH_EXPOSURE_OVER_DIVERSIFICATION_MG',
  HIGH_EXPOSURE_OVER_DIVERSIFICATION_NO_MG = 'HIGH_EXPOSURE_OVER_DIVERSIFICATION_NO_MG',
  OVER_EXPOSURE_OVER_DIVERSIFICATION_MG = 'OVER_EXPOSURE_OVER_DIVERSIFICATION_MG',
  OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG = 'OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG',
  HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_MG = 'HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_MG',
  HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG = 'HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG'
}

export function resolveReasonsToRebalanceState(
  underPerformance: LumpsumPlanRecommendationUnderPerformance | null,
  highExposure: LumpsumPlanRecommendationHighExposure | null,
  overExposure: LumpsumPlanRecommendationOverExposure | null,
  overDiversification: LumpsumPlanRecommendationOverDiversification | null
): ReasonsToRebalanceState {
  const highExposureCase = highExposure?.highExposureCase ?? false
  const overExposureCase = overExposure?.overExposureCase ?? false
  const overDiversificationCase = overDiversification?.overDiversificationCase ?? false
  const underperformanceCase = isUnderPerformanceCase(underPerformance)
  const hasMissedGains = underperformanceCase && (underPerformance?.missedGains ?? 0) > 0

  /* Cases existing in figma */
  // allocation:
  if (
    highExposureCase
    && underperformanceCase
    && hasMissedGains
    && !overExposureCase
    && !overDiversificationCase
  ) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_MG
  }
  if (
    highExposureCase
    && underperformanceCase
    && !hasMissedGains
    && !overExposureCase
    && !overDiversificationCase
  ) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_NO_MG
  }
  if (highExposureCase && !overExposureCase && !overDiversificationCase && !underperformanceCase) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE
  }
  if (overExposureCase && !highExposureCase && !overDiversificationCase && !underperformanceCase) {
    return ReasonsToRebalanceState.OVER_EXPOSURE
  }
  if (overDiversificationCase && !highExposureCase && !overExposureCase && !underperformanceCase) {
    return ReasonsToRebalanceState.OVER_DIVERSIFICATION
  }

  // rebalance:
  if (
    underperformanceCase
    && hasMissedGains
    && !highExposureCase
    && !overExposureCase
    && !overDiversificationCase
  ) {
    return ReasonsToRebalanceState.UNDERPERFORMANCE_MG
  }
  if (
    underperformanceCase
    && !hasMissedGains
    && !highExposureCase
    && !overExposureCase
    && !overDiversificationCase
  ) {
    return ReasonsToRebalanceState.UNDERPERFORMANCE_NO_MG
  }

  /* Cases not existing in figma */
  // allocation:
  if (highExposureCase && overExposureCase && overDiversificationCase && underperformanceCase && hasMissedGains) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_MG
  }
  if (highExposureCase && overExposureCase && overDiversificationCase && underperformanceCase && !hasMissedGains) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG
  }
  if (overExposureCase && overDiversificationCase && underperformanceCase && hasMissedGains) {
    return ReasonsToRebalanceState.OVER_EXPOSURE_OVER_DIVERSIFICATION_MG
  }
  if (overExposureCase && overDiversificationCase && underperformanceCase && !hasMissedGains) {
    return ReasonsToRebalanceState.OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG
  }
  if (highExposureCase && overDiversificationCase && underperformanceCase && hasMissedGains) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_DIVERSIFICATION_MG
  }
  if (highExposureCase && overDiversificationCase && underperformanceCase && !hasMissedGains) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_DIVERSIFICATION_NO_MG
  }
  if (highExposureCase && overExposureCase && underperformanceCase && hasMissedGains) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_MG
  }
  if (highExposureCase && overExposureCase && underperformanceCase && !hasMissedGains) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_NO_MG
  }
  if (overDiversificationCase && underperformanceCase && hasMissedGains) {
    return ReasonsToRebalanceState.OVER_DIVERSIFICATION_MG
  }
  if (overDiversificationCase && underperformanceCase && !hasMissedGains) {
    return ReasonsToRebalanceState.OVER_DIVERSIFICATION_NO_MG
  }
  if (overExposureCase && underperformanceCase && hasMissedGains) {
    return ReasonsToRebalanceState.OVER_EXPOSURE_MG
  }
  if (overExposureCase && underperformanceCase && !hasMissedGains) {
    return ReasonsToRebalanceState.OVER_EXPOSURE_NO_MG
  }
  if (highExposureCase && overExposureCase && overDiversificationCase) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION
  }
  if (overExposureCase && overDiversificationCase) {
    return ReasonsToRebalanceState.OVER_EXPOSURE_OVER_DIVERSIFICATION
  }
  if (highExposureCase && overDiversificationCase) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_DIVERSIFICATION
  }
  if (highExposureCase && overExposureCase) {
    return ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE
  }

  // rebalance:

  return ReasonsToRebalanceState.NONE
}

export function buildReasonsSubtitle(
  underPerformance: LumpsumPlanRecommendationUnderPerformance | null,
  highExposure: LumpsumPlanRecommendationHighExposure | null,
  overExposure: LumpsumPlanRecommendationOverExposure | null,
  overDiversification: LumpsumPlanRecommendationOverDiversification | null,
  exitingCategoryName?: string | null,
  enteringCategoryName?: string | null
): string {
  const highExposureCase = highExposure?.highExposureCase ?? false
  const overExposureCase = overExposure?.overExposureCase ?? false
  const overDiversificationCase = overDiversification?.overDiversificationCase ?? false
  const underperformanceCase = isUnderPerformanceCase(underPerformance)
  const hasMissedGains = underperformanceCase && (underPerformance?.missedGains ?? 0) > 0

  const subtitleParts: string[] = []
  const hasAllocationReason = highExposureCase || overExposureCase || overDiversificationCase

  if (highExposureCase) {
    subtitleParts.push('Helps reduce high exposure')
  }
  if (overExposureCase) {
    const reduceCategoryLabel = exitingCategoryName ?? ''
    const increaseCategoryLabel = enteringCategoryName ?? ''
    if (reduceCategoryLabel) {
      subtitleParts.push(`Helps reduce ${reduceCategoryLabel} exposure`)
    }
    if (increaseCategoryLabel) {
      subtitleParts.push(`Helps increase ${increaseCategoryLabel} exposure`)
    }
  }
  if (overDiversificationCase) {
    subtitleParts.push('Helps reduce over-diversification')
  }
  if (!hasAllocationReason && underperformanceCase) {
    subtitleParts.push('Better returns')
    subtitleParts.push('Excellent Power Rank')
    if (hasMissedGains) {
      subtitleParts.push('Missed gains')
    }
  }

  return subtitleParts.join(', ')
}

export function renderReasonsToRebalancePointersForState(
  state: ReasonsToRebalanceState,
  data: ReasonPointerData
) {
  const xirrDiff = data.pair.underPerformance?.xirrDiff ?? 0

  switch (state) {
    case ReasonsToRebalanceState.HIGH_EXPOSURE:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} badgeLabel="%" />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_EXPOSURE:
      return (
        <NumberedReasonPointers>
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_DIVERSIFICATION:
      return (
        <NumberedReasonPointers>
          <ReduceOverDiversificationPointer data={data} badgeLabel="%" />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_DIVERSIFICATION:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_EXPOSURE_OVER_DIVERSIFICATION:
      return (
        <NumberedReasonPointers>
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.UNDERPERFORMANCE_MG:
      return (
        <NumberedReasonPointers>
          {xirrDiff !== 0 ? <BetterReturnsPointer data={data} /> : null}
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.UNDERPERFORMANCE_NO_MG:
      return (
        <NumberedReasonPointers>
          {xirrDiff !== 0 ? <BetterReturnsPointer data={data} /> : null}
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_EXPOSURE_MG:
      return (
        <NumberedReasonPointers>
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_EXPOSURE_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_DIVERSIFICATION_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_DIVERSIFICATION_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_DIVERSIFICATION_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_DIVERSIFICATION_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_EXPOSURE_OVER_DIVERSIFICATION_MG:
      return (
        <NumberedReasonPointers>
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
          <MissedGainsPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.HIGH_EXPOSURE_OVER_EXPOSURE_OVER_DIVERSIFICATION_NO_MG:
      return (
        <NumberedReasonPointers>
          <ReduceOverExposurePointer data={data} />
          <ReduceExposurePointer data={data} />
          <IncreaseExposurePointer data={data} />
          <ReduceOverDiversificationPointer data={data} />
          <ExcellentRankPointer data={data} />
        </NumberedReasonPointers>
      )

    case ReasonsToRebalanceState.NONE:
    default:
      return null
  }
}

type NumberedPointerChildProps = {
  index?: number
  badgeLabel?: string | number
}

export function NumberedReasonPointers({ children }: { children: ReactNode }) {
  const items = Children.toArray(children).filter(Boolean)

  return (
    <>
      {items.map((child, pointerIndex) => {
        if (!isValidElement(child)) return child

        const pointer = child as ReactElement<NumberedPointerChildProps>
        return cloneElement(pointer, {
          key: pointer.key ?? pointerIndex,
          index: pointerIndex + 1,
          badgeLabel: items.length === 1 ? pointer.props.badgeLabel : undefined
        })
      })}
    </>
  )
}

/** Backend no longer sends underPerformanceCase — infer from metrics. */
function isUnderPerformanceCase(
  underPerformance: LumpsumPlanRecommendationUnderPerformance | null
): boolean {
  if (!underPerformance) return false
  return (
    (underPerformance.xirrDiff ?? 0) !== 0
    || (underPerformance.xirrDiffDurationInMonths ?? 0) > 0
    || (underPerformance.missedGains ?? 0) > 0
  )
}
